import React, { useMemo } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import type { PenaltyExposureProgressStep } from "./penaltyExposureProgressSteps";
import {
  computePenaltyExposureCountdown,
  allPenaltyExposureProgressStepsComplete,
} from "./penaltyExposureProgressSteps";
import { DASHBOARD } from "./dashboardTheme";

function StepIcon({ status }: { status: PenaltyExposureProgressStep["status"] }) {
  if (status === "complete") {
    return <CheckCircleIcon sx={{ fontSize: 16, color: "#2e7d32" }} />;
  }
  if (status === "error") {
    return <ErrorIcon sx={{ fontSize: 16, color: "#c62828" }} />;
  }
  if (status === "active") {
    return <CircularProgress size={14} thickness={5} sx={{ color: DASHBOARD.accent }} />;
  }
  return (
    <Box
      sx={{
        width: 14,
        height: 14,
        borderRadius: "50%",
        border: "1.5px solid #cbd5e1",
        boxSizing: "border-box",
      }}
    />
  );
}

function CountdownIndicator({
  countdown,
}: {
  countdown: number;
}): React.ReactElement {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        mb: 1,
      }}
      aria-label={`${countdown} steps remaining`}
    >
      <Box sx={{ position: "relative", display: "inline-flex", flexShrink: 0 }}>
        <CircularProgress
          size={44}
          thickness={4}
          sx={{ color: DASHBOARD.accent }}
        />
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography
            sx={{
              fontSize: 15,
              fontWeight: 700,
              color: DASHBOARD.header,
              lineHeight: 1,
            }}
          >
            {countdown}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

export function PenaltyExposureProgressList({
  steps,
  showStepList = true,
}: {
  steps: readonly PenaltyExposureProgressStep[];
  /** Global administrators see step labels; others see countdown only. */
  showStepList?: boolean;
}): React.ReactElement {
  const countdown = useMemo(() => computePenaltyExposureCountdown(steps), [steps]);
  const pipelineComplete = useMemo(
    () => allPenaltyExposureProgressStepsComplete([...steps]),
    [steps],
  );
  const showCountdown = !pipelineComplete;

  return (
    <Box sx={{ mt: 1 }}>
      {showCountdown ? <CountdownIndicator countdown={countdown} /> : null}
      {showStepList ? (
      <Box
        component="ul"
        sx={{
          listStyle: "none",
          m: 0,
          p: 0,
          display: "flex",
          flexDirection: "column",
          gap: 0.75,
        }}
        aria-live="polite"
        aria-busy={steps.some((s) => s.status === "active")}
      >
      {steps.map((step) => (
        <Box
          component="li"
          key={step.stepId}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            minHeight: 20,
          }}
        >
          <Box sx={{ width: 18, display: "flex", justifyContent: "center", flexShrink: 0 }}>
            <StepIcon status={step.status} />
          </Box>
          <Typography
            sx={{
              fontSize: 13,
              lineHeight: 1.35,
              color:
                step.status === "queued"
                  ? "#94a3b8"
                  : step.status === "active"
                    ? DASHBOARD.text
                    : step.status === "error"
                      ? "#c62828"
                      : DASHBOARD.text,
              fontStyle: step.status === "queued" ? "italic" : "normal",
              fontWeight: step.status === "active" ? 600 : 500,
            }}
          >
            {step.label}
          </Typography>
        </Box>
      ))}
      </Box>
      ) : null}
    </Box>
  );
}
